import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Interfaz para Tenant
 */
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: 'basic' | 'pro' | 'enterprise';
  status: 'active' | 'suspended' | 'cancelled';
  clientNumber?: number | null;
  taxStatus?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Obtiene el token de autenticación del localStorage
 */
function getAuthToken(): string | null {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
}

/**
 * DTO para crear un tenant (cliente/municipio)
 */
export interface CreateTenantDto {
  name: string;
  slug: string;
  plan?: 'basic' | 'pro' | 'enterprise';
  taxStatus?: string; // Condición tributaria
  contactInfo?: {
    email?: string;
    phone?: string;
    address?: string;
    cuit?: string; // CUIT o ID Fiscal
    ivaCondition?: string; // Condición IVA
    responsable?: string; // Persona responsable
  };
}

/**
 * API client para gestión de tenants (solo SuperAdmin)
 */
export const tenantsApi = {
  /**
   * Obtiene todos los tenants
   */
  async getAll(): Promise<Tenant[]> {
    const token = getAuthToken();
    const response = await axios.get<Tenant[]>(`${API_URL}/tenants`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Obtiene un tenant por ID
   */
  async getById(id: string): Promise<Tenant> {
    const token = getAuthToken();
    const response = await axios.get<Tenant>(`${API_URL}/tenants/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Crea un nuevo tenant
   */
  async create(data: CreateTenantDto): Promise<Tenant> {
    const token = getAuthToken();
    const response = await axios.post<Tenant>(`${API_URL}/tenants`, data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Actualiza un tenant existente
   */
  async update(id: string, data: Partial<CreateTenantDto>): Promise<Tenant> {
    const token = getAuthToken();
    const response = await axios.patch<Tenant>(`${API_URL}/tenants/${id}`, data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Elimina un tenant
   */
  async delete(id: string): Promise<void> {
    const token = getAuthToken();
    await axios.delete(`${API_URL}/tenants/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  /**
   * Cambia el plan de un tenant
   */
  async changePlan(id: string, plan: 'basic' | 'pro' | 'enterprise'): Promise<Tenant> {
    const token = getAuthToken();
    const response = await axios.patch<Tenant>(
      `${API_URL}/tenants/${id}/plan`,
      { plan },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },
};
